/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useState } from 'react';
import AutocompleteSelect from 'components/Inputs/AutocompleteSelect';
import { useFormContext } from 'react-hook-form';
import { strFromBase64 } from 'utils/convertString';
import { ReactComponent as LocationIcon } from 'assets/location_icon.svg';
import { ILocationAutocopmleteProps, ILocationData } from './types';
import { getLocationSuggestions } from 'utils/hereMaps';
// import styles from './locationAutocomplete.module.scss';
// import { useMutation } from 'react-query';
import { useStore } from 'hooks/useRootStore';

function LocationAutocomplete({
  afterSelect,
  afterMultiSelect,
  name,
  useOwnDataAsValue = true,
  ...props
}: ILocationAutocopmleteProps) {
  const {
    mainStore: { user },
  } = useStore();
  const { setValue } = useFormContext();

  const [options, setOptions] = useState<ILocationData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onSearch = useCallback(
    async (search: string) => {
      setIsLoading(true);
      const str = strFromBase64(search);
      // The backend fills the capital for every country; a missing one only means no suggestions.
      const capital = user?.selectedCountryCode.capital;
      if (user && str) {
        if (!capital) {
          console.warn(`No capital for ${user.selectedCountryCode.countryCode}: address suggestions are off`);
        } else
          try {
            const lang = user.lang || 'en';
            const res = await getLocationSuggestions(str, capital, lang);
            if (res) {
              setOptions(
                res.reduce((acc: ILocationData[], item: any) => {
                  if (item.address && item.resultType !== 'place')
                    acc.push({ name: item.address.label, placeId: item.id, position: item.position });
                  return acc;
                }, [] as ILocationData[]) || []
              );
            }
          } catch (error) {
            console.error('Error fetching location suggestions:', error);
          }
      }
      setIsLoading(false);
      return undefined;
    },
    [user]
  );

  // const { mutate, isLoading } = useMutation(getLocationSuggestions, {
  //   mutationKey: 'getLocationSuggestions',
  //   onSuccess: (res) => {
  //     setOptions(
  //       res?.reduce((acc: any[], item: any) => {
  //         if (item.address && item.resultType !== 'place')
  //           acc.push({ name: item.address.label, placeId: item.id, position: item.position });
  //         return acc;
  //       }, [] as ILocationData[]) || []
  //     );
  //   },
  // });

  const onSelectHandler = useCallback(
    (val: ILocationData) => {
      if (val) {
        const { placeId, name: dataName, ...rest } = val;
        setValue(useOwnDataAsValue ? name : `${name}Data`, { placeId, name }, { shouldValidate: true });
        afterSelect && afterSelect({ placeId, name: dataName }, rest);
      } else {
        setValue(name, null, { shouldValidate: true });
        afterSelect && afterSelect(null);
      }
    },
    [useOwnDataAsValue]
  );

  const onMultiSelectHandler = useCallback(
    (vals: ILocationData[]) => {
      if (vals && vals.length) {
        const newVals = vals.map(({ placeId, name: dataName }) => ({ placeId, name: dataName }));
        setValue(useOwnDataAsValue ? name : `${name}Data`, newVals, { shouldValidate: true });
        afterMultiSelect && afterMultiSelect(newVals, vals);
      } else {
        setValue(name, null, { shouldValidate: true });
        afterMultiSelect && afterMultiSelect(null);
      }
    },
    [useOwnDataAsValue]
  );

  return (
    <AutocompleteSelect
      {...props}
      name={useOwnDataAsValue ? name : `${name}Data`}
      data={options}
      onSelect={onSelectHandler}
      onMultiSelect={onMultiSelectHandler}
      onSearch={onSearch}
      disableFiltering
      optionLabel='name'
      optionValue='placeId'
      base64Converting
      isLoading={isLoading}
      customIcon={<LocationIcon />}
    />
  );
}

export default LocationAutocomplete;
